import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import classNames from 'classnames'
import OutcomeItem from '@/features/market/outcome-item'
import BinaryButtons from '@/components/ui/binary-buttons'
import Divider from '@/components/ui/divider'
import currencies from '@/util/currencies'
import './OutcomeList.scss'
import constants from '@/constants'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'
import questionService from '@/api/question'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import useIsMobileSize from '@/hooks/useIsMobileSize.js'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { deriveMarketUi, getVirtualOutcomesForMonoCustom } from '@/features/market/utils/marketType'
import { hasCryptoPriceTarget } from '@/features/market/utils/cryptoEvent'
import SportEventOutcomesList from './SportEventOutcomesList'
import CompactOutcomesList from './CompactOutcomesList'
import StickyTeamButtons from './StickyTeamButtons'

const OutcomeList = props => {
  const {
    className,
    style,
    outcomes,
    wager,
    selectedId,
    bet,
    onItemClick,
    question,
    currencyId = (bet && bet.currency) || currencies.OOM.id,
    noAnimation,
    inMarketPage,
    firstLabelIcon,
    secondLabelIcon,
    accordionMode = 'single', // 'single' or 'multiple'
    onPriceClick = () => {},
    onSetActiveTab = () => {},
    currentOutcomeSelection = null,
    globalPosition = null,
    compact = false,
    /* The compact row's "view all": every outcome, or only the leading pair. */
    compactExpanded = false,
    onToggleCompactExpanded = null,
  } = props
  const [animationParent] = useAutoAnimate()
  const isMobileSize = useIsMobileSize()
  const [listNode, setListNode] = useState(null)
  const listRef = useCallback(
    node => {
      animationParent(node)
      setListNode(node)
    },
    [animationParent]
  )

  // Accordion state: single mode stores one ID, multiple mode stores array of IDs
  const [openAccordionId, setOpenAccordionId] = useState(null)
  const [openAccordionIds, setOpenAccordionIds] = useState([])
  const previousSelectedIdRef = useRef(selectedId)

  // Sync accordion state with selectedId changes (only if accordion is already open)
  useEffect(() => {
    if (!inMarketPage || !question?.order_book_enabled) return
    if (previousSelectedIdRef.current === selectedId) return

    const previousSelectedId = previousSelectedIdRef.current
    previousSelectedIdRef.current = selectedId

    const hasOpenAccordion = accordionMode === 'single' ? openAccordionId !== null : openAccordionIds.length > 0

    if (hasOpenAccordion && selectedId) {
      if (accordionMode === 'single') {
        setOpenAccordionId(selectedId)
      } else {
        setOpenAccordionIds([selectedId])
      }
    } else if (hasOpenAccordion && !selectedId) {
      if (accordionMode === 'single') {
        setOpenAccordionId(null)
      } else {
        setOpenAccordionIds([])
      }
    }
  }, [selectedId, inMarketPage, question?.order_book_enabled, accordionMode, openAccordionId, openAccordionIds])

  if (!outcomes || !outcomes.length) {
    console.warn('Empty OutcomeList')
    return null
  }

  const marketTypeId = currencyId === currencies.OOM.id ? currencies.OOM.id : question.canonical_currency
  let wagerId = wager && typeof wager === 'object' ? wager.id : wager

  const sortedOutcomes = Array.from(outcomes)
  const ui = useMemo(() => deriveMarketUi(question), [question])
  const displayMode = ui.displayMode
  const isResolved =
    questionService.isResolved(question) ||
    question?.status === QUESTION_STATUS.RESOLVED ||
    sortedOutcomes.some(o => o.status === QUESTION_STATUS.RESOLVED)

  /* Every fixture, sided (soccer's home / draw / away) or sold as one outcome (NFL, MLB,
     MMA: long label home, short label away), draws its sides the same way. */
  if (ui.isSportsEvent && !inMarketPage) {
    return (
      <SportEventOutcomesList
        question={question}
        currencyId={currencyId}
        selectedId={selectedId}
        globalPosition={globalPosition}
        onItemClick={onItemClick}
        onSetActiveTab={onSetActiveTab}
        ui={ui}
        sortedOutcomes={sortedOutcomes}
        compact={compact}
      />
    )
  }

  /* The list view's rows have one line to spend on odds, so every non-fixture market
     — binary, mono custom or multi-outcome — renders the compact block instead of the
     card's full-width buttons or outcome rows. */
  if (compact && !inMarketPage) {
    return (
      <CompactOutcomesList
        question={question}
        ui={ui}
        sortedOutcomes={sortedOutcomes}
        currencyId={currencyId}
        selectedId={selectedId}
        globalPosition={globalPosition}
        onItemClick={onItemClick}
        onSetActiveTab={onSetActiveTab}
        isResolved={isResolved}
        expanded={compactExpanded}
        onToggleExpanded={onToggleCompactExpanded}
      />
    )
  }

  if (displayMode === 'buttons') {
    // Desktop market page: no binary row here (e.g. order book / chart handles selection)
    if (inMarketPage && !isMobileSize) {
      return null
    }

    const firstOutcome = sortedOutcomes[0]
    const needsTwoOutcomes = !ui.isMono
    const secondOutcome = sortedOutcomes[1]

    if (firstOutcome && (!needsTwoOutcomes || secondOutcome)) {
      let firstPercentage, secondPercentage

      if (ui.isMono) {
        const outcomePrice = firstOutcome.price[marketTypeId] || 0
        firstPercentage = Math.round(100 * outcomePrice)
        secondPercentage = Math.round(100 * (1 - outcomePrice))
      } else {
        firstPercentage = Math.round(100 * (firstOutcome.price[marketTypeId] || 0))
        secondPercentage = Math.round(100 * (secondOutcome.price[marketTypeId] || 0))
      }

      const labels = getPositionLabels(firstOutcome, question)

      // Mono: winning side comes from resolution_display. Linked binary: from question.resolution.
      let firstWon = false
      let secondWon = false
      if (isResolved) {
        if (ui.isMono) {
          const resolution = firstOutcome.resolution_display
          firstWon =
            Boolean(resolution) &&
            (resolution === firstOutcome.long_label || resolution === labels.longLabel)
          secondWon =
            Boolean(resolution) &&
            (resolution === firstOutcome.short_label || resolution === labels.shortLabel)
        } else {
          firstWon = question.resolution === firstOutcome.id
          secondWon = question.resolution === secondOutcome?.id
        }
      }

      const handleFirstButtonClick = () => {
        if (isResolved || !onItemClick) return
        onSetActiveTab('buy')
        if (ui.isMono) {
          onItemClick(firstOutcome, constants.position.LONG)
        } else {
          onItemClick(firstOutcome)
        }
      }

      const handleSecondButtonClick = () => {
        if (isResolved || !onItemClick) return
        onSetActiveTab('buy')
        if (ui.isMono) {
          onItemClick(firstOutcome, constants.position.SHORT)
        } else {
          onItemClick(secondOutcome)
        }
      }

      // Crypto price cards carry the odds on the buttons, as the market page does, instead
      // of the arrow and the hover reveal.
      const oddsOnButtons = inMarketPage || hasCryptoPriceTarget(question)

      return (
        <BinaryButtons
          firstButton={{
            label: labels.longLabel,
            icon: oddsOnButtons ? null : firstLabelIcon,
            value: oddsOnButtons && !isResolved ? `${firstPercentage}%` : null,
            hoverContent: isResolved || oddsOnButtons ? null : `${firstPercentage}%`,
            onClick: handleFirstButtonClick,
            outcomeId: firstOutcome.id,
            disabled: isResolved,
            featured: firstWon,
            dimmed: isResolved && (firstWon || secondWon) && !firstWon,
          }}
          secondButton={{
            label: labels.shortLabel,
            icon: oddsOnButtons ? null : secondLabelIcon,
            value: oddsOnButtons && !isResolved ? `${secondPercentage}%` : null,
            hoverContent: isResolved || oddsOnButtons ? null : `${secondPercentage}%`,
            onClick: handleSecondButtonClick,
            outcomeId: ui.isMono ? firstOutcome.id : secondOutcome?.id,
            disabled: isResolved,
            featured: secondWon,
            dimmed: isResolved && (firstWon || secondWon) && !secondWon,
          }}
          selectedId={isResolved ? null : selectedId}
          size={inMarketPage && !isMobileSize ? 'large' : 'medium'}
          renderPosition={inMarketPage && isMobileSize ? 'bottom-sticky' : null}
        />
      )
    }
  }

  // Rows run edge to edge on the mobile market page, so they are separated by a rule
  // between them rather than a border under each one.
  const withOutcomeDividers = items =>
    inMarketPage && isMobileSize
      ? items.flatMap((item, index) => (index === 0 ? [item] : [<Divider key={`outcome-divider-${index}`} />, item]))
      : items

  const handleAccordionClick = outcomeId => {
    if (!inMarketPage) return

    if (accordionMode === 'single') {
      setOpenAccordionId(prevId => (prevId === outcomeId ? null : outcomeId))
    } else {
      setOpenAccordionIds(prevIds => {
        if (prevIds.includes(outcomeId)) {
          return prevIds.filter(id => id !== outcomeId)
        } else {
          return [...prevIds, outcomeId]
        }
      })
    }
  }

  const isAccordionOpenForOutcome = outcomeId => {
    if (!inMarketPage) return false
    return accordionMode === 'single' ? openAccordionId === outcomeId : openAccordionIds.includes(outcomeId)
  }

  /* A two-sided fixture on the mobile market page keeps both sides reachable from a
     bottom-sticky bar while its rows are off screen. Each side mirrors its row's bet button. */
  const renderStickyTeamButtons = sides => {
    if (!ui.isSportsEvent || !inMarketPage || !isMobileSize || isResolved || !onItemClick) return null
    if (sides.length !== 2 || sides.some(side => side.outcome?.status !== 'active')) return null

    const toButton = side => ({
      label: side.label,
      value: `${Math.round(100 * (side.price?.[marketTypeId] || 0))}%`,
      outcomeId: side.key,
      /* Same neutral fill as the fixture card's side buttons. Like the rows' own bet
         buttons on mobile, they never show a selected state: the open sheet carries it. */
      className: 'border border-solid border-transparent bg-surface-3 text-Neutral-6 hover:border-Neutral-6',
      onClick: () => {
        onSetActiveTab('buy')
        onItemClick(side.outcome, side.position)
      },
    })

    return <StickyTeamButtons listNode={listNode} firstButton={toButton(sides[0])} secondButton={toButton(sides[1])} />
  }

  const renderList = (items, stickySides) => (
    <>
      <div
        className={classNames('outcome-list', `outcome-list--${inMarketPage ? 'market' : 'feed'}`, className, { '!justify-center': (outcomes?.length ?? 0) <= 2 })}
        ref={listRef}
        style={style}>
        {withOutcomeDividers(items)}
      </div>
      {renderStickyTeamButtons(stickySides)}
    </>
  )

  // Independent mono custom: create virtual outcomes (LONG/SHORT) from single physical outcome
  if (ui.needsVirtualOutcomes && sortedOutcomes.length === 1) {
    const { actualOutcome, virtualOutcomes: baseVirtualOutcomes } = getVirtualOutcomesForMonoCustom(question)
    if (!actualOutcome || baseVirtualOutcomes.length !== 2) return null

    const virtualOutcomes = baseVirtualOutcomes.map(v => ({
      ...actualOutcome,
      id: actualOutcome.id,
      virtualId: v.virtualId,
      title: v.title,
      price: v.price,
      // After the spread, so each stack-bar row keeps its own position image.
      thumbnail: v.thumbnail,
      _virtualPosition: v.position,
      _actualOutcome: actualOutcome,
    }))

    const outcomesToRender = virtualOutcomes.map(virtualOutcome => {
      const handleOutcomeClick = (clickedOutcome, position) => {
        if (!onItemClick) return
        onSetActiveTab('buy')
        onItemClick(actualOutcome, virtualOutcome._virtualPosition)
      }

      // selectedId uses virtualId format for Case 5 to sync with BuyPlaceBet dropdown
      const isSelected = selectedId
        ? virtualOutcome.virtualId === selectedId
        : globalPosition === virtualOutcome._virtualPosition

      return (
        <OutcomeItem
          key={`outcome-item-${virtualOutcome.virtualId}`}
          outcome={virtualOutcome}
          bet={wager ? actualOutcome.id === wagerId : null}
          prediction={bet}
          selected={isSelected}
          onClick={onItemClick ? handleOutcomeClick : null}
          parentQuestion={question}
          currencyId={marketTypeId}
          question={question}
          disabled={false}
          noAnimation={noAnimation}
          inMarketPage={inMarketPage}
          isAccordionOpen={isAccordionOpenForOutcome(virtualOutcome.virtualId)}
          onAccordionToggle={() => handleAccordionClick(virtualOutcome.virtualId)}
          displayMode={isMobileSize && inMarketPage ? 'binary' : 'segment'}
          onPriceClick={onPriceClick}
          currentOutcomeSelection={currentOutcomeSelection}
          globalPosition={globalPosition}
          marketType={ui.kind}
          shouldShowPositionButtons={false}
          outcomeLength={virtualOutcomes.length}
        />
      )
    })

    return renderList(
      outcomesToRender,
      virtualOutcomes.map(v => ({
        key: v.virtualId,
        label: v.title,
        price: v.price,
        outcome: actualOutcome,
        position: v._virtualPosition,
      }))
    )
  }

  const outcomesToRender = sortedOutcomes.map(outcome => {
    const shouldShowPositionButtons = displayMode === 'list-with-buttons'
    
    const handleOutcomeClick = (clickedOutcome, position) => {
      if (!onItemClick) return
      onSetActiveTab('buy')
      const outcomeToSend = ui.placeBet.outcomeSource === 'first' ? sortedOutcomes[0] : clickedOutcome
      const positionToSend =
        ui.placeBet.positionSource === 'alwaysLong'
          ? constants.position.LONG
          : position || constants.position.LONG
      onItemClick(outcomeToSend, positionToSend)
    }

    return (
      
      <OutcomeItem
        key={`outcome-item-${outcome.id}`}
        outcome={outcome}
        bet={wager ? outcome.id === wagerId : null}
        prediction={bet}
        selected={selectedId ? outcome.id === selectedId : null}
        onClick={onItemClick ? handleOutcomeClick : null}
        parentQuestion={question}
        currencyId={marketTypeId}
        question={question}
        disabled={false}
        noAnimation={noAnimation}
        inMarketPage={inMarketPage} 
        isAccordionOpen={isAccordionOpenForOutcome(outcome.id)}
        onAccordionToggle={() => handleAccordionClick(outcome.id)}
        displayMode={isMobileSize && inMarketPage ? 'binary' : 'segment'}
        onPriceClick={onPriceClick}
        currentOutcomeSelection={currentOutcomeSelection}
        globalPosition={globalPosition}
        marketType={ui.kind}
        shouldShowPositionButtons={shouldShowPositionButtons}
        outcomeLength={sortedOutcomes.length}
      />
    )
  })


  return renderList(
    outcomesToRender,
    sortedOutcomes.map(outcome => ({
      key: outcome.id,
      label: outcome.title,
      price: outcome.price,
      outcome,
      position: constants.position.LONG,
    }))
  )
}

export default OutcomeList
